import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { Company } from "../../../models/company";
import { CompanyService } from "../../../services/company/company.service";
import { TableComponent } from "../../ui/table/table.component";
import { BaseComponent } from "../../base/base.component";
import { User } from "../../../models/user";
import Swal from "sweetalert2";
import { AgentService } from "../../../services/agent/agent.service";
import { AthleteService } from "../../../services/athlete/athlete.service";
import { StaffService } from "../../../services/staff/staff.service";
import { AthleteSeason } from "../../../models/athleteSeason";
import { PaymentService } from '../../../services/payment/payment.service';

type DashboardView = 'list' | 'cards';
const DASHBOARD_VIEW_KEY = 'wisper_adminDashboardView';

@Component({
    selector: 'app-dashboard-admin',
    templateUrl: './dashboard-admin.component.html',
    styleUrls: ['./dashboard-admin.component.scss']
})
export class DashboardAdminComponent extends BaseComponent implements OnInit {

    @ViewChild('companiesTable') companiesTable: TableComponent | undefined;
    @ViewChild('agentsTable') agentTable: TableComponent | undefined;
    @ViewChild('staffsTable') staffTable: TableComponent | undefined;
    @ViewChild('lastPendingPaymentRegistration') lastPendingPaymentRegistration: TableComponent | undefined;

    @ViewChild('paymentsList') paymentsList: TableComponent | undefined;

    payments: any[] = [];
    companyId: string = '';
    paymentMethods: string[] = ['transfer', 'localPayment', 'nexi', 'nexiRates', 'appPagoRates'];

    editingPaymentId: string | null = null;
    editAmount: number = 0;

    athleteTextFilter: string = '';
    selectedPaymentMethod: string | null = '';
    // Collapsible states
    collapsePendingPayments: boolean = true;
    collapsePaymentsList: boolean = true;
    collapseCompaniesTable: boolean = true;
    collapseAgentsTable: boolean = true;

    companies: any[] = [];
    selectedCompany: any = null;
    selectedPayment: any = null;
    selectedStatus: string = 'paid';
    approvingCompanyIds: Record<string, boolean> = {};

    /** Vista della dashboard: 'list' (classica, predefinita) o 'cards' (nuova). Salvata per browser. */
    viewMode: DashboardView = localStorage.getItem(DASHBOARD_VIEW_KEY) === 'cards' ? 'cards' : 'list';
    expandedCardSections = new Set<string>();
    readonly cardPreviewSize = 5;

    constructor(public companyService: CompanyService,
        private paymentService: PaymentService,
        private agentService: AgentService,
        private athleteService: AthleteService,
        private staffService: StaffService,
        public serviceInjector: Injector) {

        super(serviceInjector);
    }

    async ngOnInit() {
        /*let stats = await this.statsService.getAdminStatsTotal();

        if (stats.success) this.stats = stats.data;*/

        /*Object.keys(stats)
            .forEach( (key: any) => {
              this.stats.push(stats[key])
            })*/
        this.companies = await this.companyService.getCompanies({ $orderby: 'name asc' });
        await this.loadPayments();
    }

  ngAfterViewInit() {
    this.companiesTable?.loadData('/companies/pendingApproval', {
      onlyPendingApprovation: 1
    }, (item: any) => {
      return new Company(item)
    })

        this.agentTable?.loadData('/users', { userGroupId: 'agent', status: 'pendingApprovation' }, (item: any) => {
            return new User(item)
        })

      this.staffTable?.loadData('/users', { userGroupId: 'staff', status: 'pendingApprovation' }, (item: any) => {
            return new User(item)
        })

        this.lastPendingPaymentRegistration?.loadData('/athleteSeasonsToConfirmPayment', {}, (item: any) => {
            return new AthleteSeason(item)
        })
    }

    // ---- Vista a schede -------------------------------------------------------

    setViewMode(mode: DashboardView) {
        this.viewMode = mode;
        localStorage.setItem(DASHBOARD_VIEW_KEY, mode);
    }

    get pendingPaymentRows(): any[] { return this.lastPendingPaymentRegistration?.rows ?? []; }
    get pendingCompanyRows(): any[] { return this.companiesTable?.rows ?? []; }
    get pendingAgentRows(): any[] { return this.agentTable?.rows ?? []; }
    get paymentRows(): any[] { return this.paymentsList?.rows ?? []; }

    countOf(table: TableComponent | undefined): number {
        return table ? (table.totalCount || table.rows.length) : 0;
    }

    visibleRows(section: string, rows: any[]): any[] {
        return this.expandedCardSections.has(section) ? rows : rows.slice(0, this.cardPreviewSize);
    }

    toggleCardSection(section: string) {
        if (this.expandedCardSections.has(section)) this.expandedCardSections.delete(section);
        else this.expandedCardSections.add(section);
    }

    scrollToCard(id: string) {
        document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }

    initials(first?: string, last?: string): string {
        return `${(first ?? '').trim().charAt(0)}${(last ?? '').trim().charAt(0)}`.toUpperCase() || '?';
    }

    companyStatus(status?: string): { label: string; classes: string } {
        switch (status) {
            case 'pendingApprovation': return {label: 'Da approvare', classes: 'bg-amber-50 text-amber-800'};
            case 'pendingOnboarding': return {label: 'In onboarding', classes: 'bg-sky-50 text-sky-800'};
            case 'active': return {label: 'Approvata', classes: 'bg-emerald-50 text-emerald-700'};
            default: return {label: status || '—', classes: 'bg-slate-100 text-slate-600'};
        }
    }

    paymentStatus(status?: string): { label: string; classes: string } {
        switch (status) {
            case 'paid': return {label: 'Pagato', classes: 'bg-emerald-50 text-emerald-700'};
            case 'verifying payment':
            case 'verifyingPayment': return {label: 'In verifica', classes: 'bg-amber-50 text-amber-800'};
            default: return {label: status || '—', classes: 'bg-red-50 text-red-700'};
        }
    }

    isCompanyApprovalBusy(companyId: string): boolean {
        return !!this.approvingCompanyIds[companyId];
    }

    async approveCompany(companyId: string) {
        if (this.approvingCompanyIds[companyId]) {
            return;
        }

        this.approvingCompanyIds = {...this.approvingCompanyIds, [companyId]: true};
        try {
            const response = await this.companyService.approveCompany(companyId);

            if (response?.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Società accettata!'
                });
                this.companiesTable?.reloadPage();
            } else {
                Swal.fire({
                    icon: 'error',
                    title: "Errore durante l'operazione"
                });
            }
        } finally {
            const next = {...this.approvingCompanyIds};
            delete next[companyId];
            this.approvingCompanyIds = next;
        }
    }

    async sendApprovalEmail(companyId: string) {
        const response = await this.companyService.sendApprovalEmail(companyId);

        if (response.success) {
            Swal.fire({
                icon: 'success',
                title: 'Email di Onboarding inviata con successo!'
            });
        } else {
            Swal.fire({
                icon: 'error',
                title: "Errore durante l'operazione"
            });
        }
    }

    async refuseCompany(companyId: string) {
        if (this.isCompanyApprovalBusy(companyId)) {
            return;
        }

        const response = await this.companyService.refuseCompany(companyId);

        if (response.success) {
            Swal.fire({
                icon: 'success',
                title: 'Società rifiutata!'
            });
            this.companiesTable?.reloadPage();
        } else {
            Swal.fire({
                icon: 'error',
                title: "Errore durante l'operazione"
            });
        }
    }

    async approveAgent(agentId: string) {
        this.agentService.approve(agentId).subscribe((response: any) => {
            if (response.success) {
                this.showSuccessMessage('Agente approvato con successo!')
                this.agentTable?.reloadPage();
            } else {
                this.showErrorMessage('Errore durante l\'approvazione dell\'agente')
            }
        });
    }

    async rejectAgent(agentId: string) {
        this.agentService.reject(agentId).subscribe((response: any) => {
            if (response.success) {
                this.showSuccessMessage('Agente rifiutato!')
                this.agentTable?.reloadPage();
            } else {
                this.showErrorMessage('Errore, impossibile rifiutare l\'agente')
            }
        });
    }



    approveAthletePayment(paymentId: string) {
        Swal.fire({
            icon: "question",
            title: "Sei sicuro di voler approvare il pagamento dell'atleta?",
            showCancelButton: true,
            confirmButtonText: 'Si, procedi',
            cancelButtonText: "Annulla l'operazione"
        })
            .then(async (result) => {
                if (result.isConfirmed) {
                    this.spinner.show();
                    let athleteResponse = await this.athleteService.approveAthletePaymentRequest(paymentId);
                    this.spinner.hide();

                    if (athleteResponse) {
                        Swal.fire({
                            icon: 'success',
                            title: 'Pagamento approvato con successo'
                        })
                        this.lastPendingPaymentRegistration?.reloadPage();
                    } else {
                        Swal.fire({
                            icon: 'error',
                            title: "Errore nell'approvazione del pagamento"
                        })
                    }
                }
            });
    }

    rejectAthletePayment(paymentId: string) {
        Swal.fire({
            icon: "question",
            title: "Sei sicuro di voler rifiutare il pagamento dell'atleta?",
            showCancelButton: true,
            confirmButtonText: 'Si, procedi',
            cancelButtonText: "Annulla l'operazione"
        })
            .then(async (result) => {
                if (result.isConfirmed) {
                    this.spinner.show();
                    let athleteResponse = await this.athleteService.rejectAthletePaymentRequest(paymentId);
                    this.spinner.hide();

                    if (athleteResponse) {
                        Swal.fire({
                            icon: 'success',
                            title: 'Pagamento respinto con successo'
                        })
                        this.lastPendingPaymentRegistration?.reloadPage();
                    } else {
                        Swal.fire({
                            icon: 'error',
                            title: "Errore, non è stato possibile respingere il pagamento"
                        })
                    }
                }
            });
    }

    async loadPayments() {
        const params: any = {};
        if (this.selectedStatus) params['status'] = this.selectedStatus;

        console.log("selectedCompany: ", this.selectedCompany)
        if (!this.isSuperAdmin()) {
            params['companyId'] = this.companyId;
        } else if (this.selectedCompany && this.selectedCompany[0]?.id) {
            params['companyId'] = this.selectedCompany[0]?.id;
        }
        if (this.athleteTextFilter) params['athleteText'] = this.athleteTextFilter;
        if (this.selectedPaymentMethod) params['paymentMethod'] = this.selectedPaymentMethod;

        this.paymentsList?.loadData(
            `/allAthletePaymentRequests`,
            params,
            (item: any) => item,
            true
        );
        console.log("lista pagamenti ->", this.paymentsList);
    }

    filterSearch() {
        this.loadPayments();
    }

    async deletePayment(paymentId: string) {
        const result = await Swal.fire({
            icon: 'warning',
            title: "Sei sicuro di voler eliminare il pagamento?",
            text: "Non si potrà più tornare indietro",
            showCancelButton: true,
            confirmButtonText: "Sì, procedi",
            cancelButtonText: "No, annulla"
        });

        if (!result.isConfirmed) {
            return;
        }

        try {
            this.showSpinner();

            const response = await this.athleteService.deletePaymentRequest(paymentId);

            if (response?.success) {
                this.showSuccessMessage('Pagamento eliminato con successo');
                this.loadPayments();
            } else {
                this.showErrorMessage(
                    response?.error || "Errore durante l'eliminazione del pagamento"
                );
            }

        } catch (error) {
            this.showErrorMessage("Errore imprevisto durante l'eliminazione");
        } finally {
            this.hideSpinner();
        }
    }

    canEdit(payment: any): boolean {
        console.log("canEdit payment: ", payment)
        return payment.paymentMethod === 'transfer' || payment.paymentMethod === 'localPayment';
    }

    startEdit(payment: any) {
        if (!this.canEdit(payment)) return;

        this.editingPaymentId = payment.id;
        this.editAmount = payment.amount;
    }

    cancelEdit() {
        this.editingPaymentId = null;
        this.editAmount = 0;
    }

    getPaymentMethodLabel(method: string): string {
        switch (method) {
            case 'transfer':
                return 'Bonifico';
            case 'localPayment':
                return 'Pagamento in segreteria';
            case 'nexi':
                return 'Carta di Credito (Nexi)';
            case 'nexiRates':
                return 'Rateale (Nexi)';
            case 'appPagoRates':
                return 'Rateale (AppPago)';
            default:
                return method;
        }
    }

    resetFilters() {
        this.athleteTextFilter = '';
        this.selectedPaymentMethod = '';
        this.selectedCompany = null;
        this.selectedStatus = 'paid';
        this.filterSearch();
    }

    companySettings = {
        singleSelection: true,
        idField: 'id',
        textField: 'name',
        allowSearchFilter: true,
        closeDropDownOnSelection: true,
        clearSearchFilter: true
    }

    openEditModal(payment: any) {
        this.selectedPayment = payment;
        this.editAmount = payment.amount;
    }

    closeEditModal() {
        this.selectedPayment = null;
        this.editAmount = 0;
    }

    async confirmEdit() {
        if (this.editAmount <= 0) {
            this.showErrorMessage("L'importo deve essere maggiore di zero");
            return;
        }
        try {
            this.showSpinner();
            const response = await this.paymentService.updatePaymentAmount(this.selectedPayment.id, this.editAmount, 'paid');

            if (response?.success) {
                this.showSuccessMessage("Importo aggiornato con successo");
                this.closeEditModal();
                this.loadPayments();
            } else {
                this.showErrorMessage(response?.error || "Errore durante l'aggiornamento");
            }

        } catch (error) {
            this.showErrorMessage("Errore imprevisto");
        } finally {
            this.hideSpinner();
        }
    }
}

